type LngLat = [number, number];

const EARTH_RADIUS = 6371008.8;
const CIRCLE_STEPS = 128;

const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
const toDegrees = (radians: number) => (radians * 180) / Math.PI;

export const normalizeDegrees = (degrees: number) => ((degrees % 360) + 360) % 360;

export const getDistance = ([lng1, lat1]: LngLat, [lng2, lat2]: LngLat) => {
  const a =
    Math.sin(toRadians(lat2 - lat1) / 2) ** 2 + Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(toRadians(lng2 - lng1) / 2) ** 2;
  return 2 * EARTH_RADIUS * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const getPathDistance = (points: LngLat[]) =>
  points.reduce((total, point, index) => (index === 0 ? total : total + getDistance(points[index - 1], point)), 0);

export const getBearing = ([lng1, lat1]: LngLat, [lng2, lat2]: LngLat) => {
  const phi1 = toRadians(lat1);
  const phi2 = toRadians(lat2);
  const deltaLambda = toRadians(lng2 - lng1);
  const y = Math.sin(deltaLambda) * Math.cos(phi2);
  const x = Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(deltaLambda);
  return normalizeDegrees(toDegrees(Math.atan2(y, x)));
};

export const getDestination = ([lng, lat]: LngLat, distance: number, bearing: number): LngLat => {
  const delta = distance / EARTH_RADIUS;
  const theta = toRadians(bearing);
  const phi1 = toRadians(lat);
  const phi2 = Math.asin(Math.sin(phi1) * Math.cos(delta) + Math.cos(phi1) * Math.sin(delta) * Math.cos(theta));
  const lambda2 = toRadians(lng) + Math.atan2(Math.sin(theta) * Math.sin(delta) * Math.cos(phi1), Math.cos(delta) - Math.sin(phi1) * Math.sin(phi2));
  return [toDegrees(lambda2), toDegrees(phi2)];
};

export const getCircle = (center: LngLat, radius: number): LngLat[] => {
  const ring = Array.from({ length: CIRCLE_STEPS }, (_, step) => getDestination(center, radius, (step * 360) / CIRCLE_STEPS));
  return [...ring, ring[0]];
};

export const getArea = (points: LngLat[]) => {
  if (points.length < 3) {
    return 0;
  }
  const total = points.reduce((sum, _, index) => {
    const lower = points[index];
    const middle = points[(index + 1) % points.length];
    const upper = points[(index + 2) % points.length];
    return sum + (toRadians(upper[0]) - toRadians(lower[0])) * Math.sin(toRadians(middle[1]));
  }, 0);
  return Math.abs((total * EARTH_RADIUS * EARTH_RADIUS) / 2);
};

export const getPerimeter = (points: LngLat[]) => (points.length < 3 ? getPathDistance(points) : getPathDistance([...points, points[0]]));

export const getBoundsCenter = (points: LngLat[]): LngLat => {
  const lngs = points.map(([lng]) => lng);
  const lats = points.map(([, lat]) => lat);
  return [(Math.min(...lngs) + Math.max(...lngs)) / 2, (Math.min(...lats) + Math.max(...lats)) / 2];
};

export const getMidpoint = ([lng1, lat1]: LngLat, [lng2, lat2]: LngLat): LngLat => [(lng1 + lng2) / 2, (lat1 + lat2) / 2];
